import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {ControlButton, SelectInput, TextInput, TscButtonType} from '@techsmith/tsc-cloud-style-guide';
import Optional from '../../model/optional';
import config from '../../service/config';
import conversationApi from '../../service/conversationApi';
import folderApi from '../../service/folderApi';
import mediaApi from '../../service/mediaApi';
import {IChaptersMinMax, ISuggestedChapter, ISuggestedDescription, ISuggestedTitle} from '../../model/mediaIntelligence';
import {StyledWarning} from '../util/StyledElements';
import '../../static/css/edit-details-modal.less';
import '../../static/css/modal.less';
import decode from 'entity-decode';
import {IWorkerState, SetupWorkerState} from '../../model/workerState';
import {IFieldData} from '../../model/fieldData';
import {conversationsStore} from '../../context/conversationsProvider';
import {useTscTheme} from '../../context/themeProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {mediaStore, updateMediaState} from '../../context/mediaProvider';
import {IConversationData} from '../../model/conversationModel';
import {IMinimalFolder} from '../../model/minimalFolder';
import {IBaseLibrary} from '../../model/library';
import {BasicModalPortal} from '../util/ModalPortalHelper';
import MediaDetailsGenerateMenu, {MediaDetailGenerationTypes} from './MediaDetailsGenerateMenu';
import {CaptioningState, ICaptionData} from '../../model/captionModels';
import transcriptionApi from '../../service/transcriptionApi';
import Constants from '../../constants/Constants';
import {captionDataStore, updateCaptionDataState} from '../../context/captionDataProvider';
import captionApi from '../../service/captionApi';
import MediaDetailsChapters, {IChaptersDisplayModel} from './MediaDetailsChapters';
import {IMediaChapters} from '../../model/mediaDetails';
import TimeFormatter from '../../util/TimeFormatter';
import {MediaContentType} from '../../model/baseMedia';
import {EnvironmentType} from '../../model/appModel';

interface ILocalMediaData {
   title: IFieldData;
   description: IFieldData;
   chapters: IChaptersDisplayModel[];
}

const editDetailsModalWidth = '31rem';

const {minimumGeneratedChapters, maximumGeneratedChapters, maximumChapters} = Constants.chapters;
const defaultMinMax: IChaptersMinMax = {min: minimumGeneratedChapters, max: maximumGeneratedChapters};

const getInitialMinMax = (duration: string): IChaptersMinMax => {
   if (!duration) {
      return defaultMinMax;
   }

   const seconds = TimeFormatter.getSecondsFromTimeString(duration);

   /* eslint-disable @typescript-eslint/no-magic-numbers */
   if (seconds === 0) {
      return defaultMinMax;
   }

   const minutes = Math.floor(seconds / 60);

   if (minutes <= 3) {
      return {min: 3, max: 5};
   }
   if (minutes <= 6) {
      return {min: 4, max: 8};
   }
   if (minutes <= 20) {
      return {min: 7, max: 10};
   }
   return {min: 8, max: 15};
   /* eslint-enable @typescript-eslint/no-magic-numbers */
};

export const EditDetailsModalBase: React.FC<IEditDetailsProps & IStateMappedProps> = ({onClose, conversations, Hash, MediaId, Title, Description, ParentFolder, OwnerLibrary, IsVideo, ContentType, Duration, Chapters, captionData}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [workerState, setWorkerState] = useState<IWorkerState>({
      isWorking: false,
      hasError: false
   });
   const [loadingCaptionData, setLoadingCaptionData] = SetupWorkerState();
   const [captionStatus, setCaptionStatus] = useState<CaptioningState>(CaptioningState.None);
   const [minMax, setMinMix] = useState<IChaptersMinMax>(getInitialMinMax(Duration));
   const [aiRequestWasTooLarge, setAiRequestWasTooLarge] = useState(false);
   const [aiRequestContentFiltered, setAiRequestContentFiltered] = useState(false);
   const [parentFolderHash, setParentFolderHash] = useState(ParentFolder ? ParentFolder.Hash : '');
   const [chaptersHaveInputError, setChaptersHaveInputError] = useState(false);
   const [localMediaData, setLocalMediaData] = useState<ILocalMediaData>({
      title: {
         value: Title || 'Title'
      },
      description: {
         value: Description
      },
      chapters: Chapters ? Chapters.chapters.map(chapter => ({timeCode: TimeFormatter.formatTime(chapter.timeInMilliseconds / 1000), description: chapter.label} as IChaptersDisplayModel)) : []
   });

   const {
      title: {
         value: title,
         errorText: titleErrorText
      },
      description: {
         value: description,
         errorText: descriptionErrorText
      },
      chapters
   } = localMediaData;

   const isDev = config?.environmentData?.Environment === EnvironmentType.dev;

   const onTitleChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
      const newTitle = evt.target.value;
      updateTitle(newTitle);
   };

   const updateTitle = (newTitle: string): void => {
      const isEmpty = !newTitle.trim();
      const isTooLong = newTitle.length > config.watchConfig.maxTitleLength;

      setLocalMediaData({
         ...localMediaData,
         title: {
            value: newTitle,
            errorText: isEmpty
               ? t('details.title.requiredError')
               : isTooLong
                  ? t('details.title.maxLengthError', {0: config.watchConfig.maxTitleLength})
                  : ''
         }
      });
   };

   const updateDescription = (newDescription: string): void => {
      // use the Symbol.iterator of string to handle multi width characters
      const isTooLong = [...newDescription].length > config.watchConfig.maxDescriptionLength;

      setLocalMediaData({
         ...localMediaData,
         description: {
            value: newDescription,
            errorText: isTooLong
               ? t('details.description.maxLengthError', {0: config.watchConfig.maxDescriptionLength})
               : ''
         }
      });
   };

   const onDescriptionChange = (evt: React.ChangeEvent<HTMLInputElement>): void => {
      const newDescription = evt.target.value;
      updateDescription(newDescription);
   };

   const onChaptersUpdate = (newChapters: IChaptersDisplayModel[]): void => {
      setLocalMediaData({
         ...localMediaData,
         chapters: newChapters
      });
   };

   const onChaptersInputError = (hasError: boolean): void => {
      setChaptersHaveInputError(hasError);
   };

   const onGenerateMediaDetailsCallback = async (type: MediaDetailGenerationTypes) => {
      if (type === MediaDetailGenerationTypes.description) {
         try {
            setWorkerState({isWorking: true, hasError: false});
            const response: ISuggestedDescription = await mediaApi.getSuggestedDescription(Hash);
            if (response?.suggestion) {
               updateDescription(response.suggestion);
               setWorkerState({isWorking: false, hasError: false});
            }
         } catch (e) {
            setAiRequestWasTooLarge(e.status === Constants.statusCodes.contentTooLarge);
            setAiRequestContentFiltered(e.status === Constants.statusCodes.forbidden);
            setWorkerState({isWorking: false, hasError: true});
            console.error('Error generating media description: ', e);
         }
      } else if (type === MediaDetailGenerationTypes.title) {
         try {
            setAiRequestWasTooLarge(true);
            setWorkerState({isWorking: true, hasError: false});
            const response: ISuggestedTitle = await mediaApi.getSuggestedTitle(Hash);
            if (response?.suggestion) {
               setAiRequestWasTooLarge(false);
               updateTitle(response.suggestion);
               setWorkerState({isWorking: false, hasError: false});
            }
         } catch (e) {
            setAiRequestWasTooLarge(e.status === Constants.statusCodes.contentTooLarge);
            setAiRequestContentFiltered(e.status === Constants.statusCodes.forbidden);
            setWorkerState({isWorking: false, hasError: true});
            console.error('Error generating media title: ', e);
         }
      } else if (type === MediaDetailGenerationTypes.chapters) {
         try {
            setAiRequestWasTooLarge(true);
            setWorkerState({isWorking: true, hasError: false});
            const suggestedChapters: ISuggestedChapter[] = await mediaApi.getSuggestedChapters(Hash, minMax.min, minMax.max);
            if (suggestedChapters.length > 0) {
               setAiRequestWasTooLarge(false);
               suggestedChapters[0].time = 0; // First chapter always starts at 00:00
               const updatedChapters: IChaptersDisplayModel[] = suggestedChapters.map(chapter => ({timeCode: TimeFormatter.formatTime(chapter.time), description: chapter.title, timeCodeValid: true, descriptionValid: true}));
               onChaptersUpdate(updatedChapters);
               setWorkerState({isWorking: false, hasError: false});
            }
         } catch (e) {
            setAiRequestWasTooLarge(e.status === Constants.statusCodes.contentTooLarge);
            setAiRequestContentFiltered(e.status === Constants.statusCodes.forbidden);
            setWorkerState({isWorking: false, hasError: true});
            console.error('Error generating media chapters: ', e);
         }
      }
   };

   const setMinMaxCallback = (updatedMinMax: IChaptersMinMax) => {
      let min = updatedMinMax.min;
      let max = updatedMinMax.max;

      if (min !== minMax.min && min > max) {
         max = min;
      }

      if (max !== minMax.max && max < min) {
         min = max;
      }

      setMinMix({min: Math.max(minimumGeneratedChapters, min), max: Math.min(maximumChapters, max)});
   };

   const getCaptioningStatus = async () => {
      setLoadingCaptionData({isWorking: true, hasError: false});
      try {
         const currentCaptionStatus = await captionApi.getStatus(MediaId);
         updateCaptionDataState(currentCaptionStatus);
         if (currentCaptionStatus.length === 0) {
            setCaptionStatus(CaptioningState.None);
         } else {
            setCaptionStatus(currentCaptionStatus[0].CaptioningState);
         }
         setLoadingCaptionData({isWorking: false, hasError: false});
      } catch (e) {
         console.error(e);
         setLoadingCaptionData({isWorking: false, hasError: true});
      }
   };

   useEffect(() => {
      if (!captionData?.length) {
         void getCaptioningStatus();
      } else {
         setCaptionStatus(captionData[0].CaptioningState);
      }
   }, []);

   const updateParentFolder = (evt: React.ChangeEvent<HTMLSelectElement>): void => {
      const newParentFolderHash = evt.target.value;
      setParentFolderHash(newParentFolderHash);
   };

   const supportsChapters = () => IsVideo && ContentType !== MediaContentType.YouTube;

   const validateChapters = (): boolean => chapters.length === 0 || chapters.every(chapter => chapter.timeCode && Constants.timeCodeWithNoSpaceRegex.test(chapter.timeCode) && chapter.description);

   const convertChaptersFromDisplayToSave = (): IMediaChapters => ({
      version: '1.0',
      chapters: chapters.sort((a, b) => (TimeFormatter.getSecondsFromTimeString(a.timeCode) - TimeFormatter.getSecondsFromTimeString(b.timeCode))).map((chapter: IChaptersDisplayModel) => ({
         timeInMilliseconds: TimeFormatter.getSecondsFromTimeString(chapter.timeCode) * 1000,
         label: chapter.description
      }))
   } as IMediaChapters);

   const onSave = async () => {
      setWorkerState({
         isWorking: true,
         hasError: false
      });

      const updates = [];

      if (title !== Title) {
         updates.push(mediaApi.updateTitle(Hash, title));
         if (config.featureSwitches.IsScreencast && conversations && conversations.length > 0) {
            updates.push(conversationApi.renameConversation(Hash, conversations[0].ConversationHash, title));
         }
      }

      if (description !== Description) {
         updates.push(mediaApi.updateDescription(Hash, description));
      }

      if (ParentFolder ? ParentFolder.Hash : '' !== parentFolderHash) {
         updates.push(folderApi.updateParentFolder([Hash], [], parentFolderHash, OwnerLibrary.User?.TechSmithId));
      }

      let chaptersToSave = null;
      if (supportsChapters() && validateChapters()) {
         chaptersToSave = convertChaptersFromDisplayToSave();
         updates.push(mediaApi.updateChapters(Hash, chaptersToSave));
      }

      try {
         await Promise.all(updates);

         updateMediaState({
            Title: title,
            Description: description,
            ParentFolder: config.folderMap[parentFolderHash],
            Chapters: chaptersToSave
         });

         onClose();
      } catch {
         setWorkerState({
            isWorking: false,
            hasError: true
         });
      }
   };

   const saveButton = (
      <ControlButton
         testId="edit-details-modal-save-button"
         label={t('general.save')}
         buttonType={TscButtonType.primary}
         disabled={Boolean(titleErrorText || descriptionErrorText || chaptersHaveInputError || (config.featureSwitches.IsScreencast && !conversations))}
         themeName={theme}
         onClick={onSave}
         width="7rem"
      />
   );

   const cancelButton = (
      <ControlButton
         label={t('general.cancel')}
         buttonType={TscButtonType.secondary}
         disabled={false}
         themeName={theme}
         onClick={onClose}
         width="7rem"
      />
   );

   const folderOptions = [{value: 'root', label: t('details.rootFolderName')}];
   if (config.watchConfig.folder) {
      config.watchConfig.folder.SubFolders.forEach(subfolder => {
         folderOptions.push({value: subfolder.Hash, label: subfolder.Name});
         subfolder.SubFolders.forEach(subsubfolder => folderOptions.push({value: subsubfolder.Hash, label: ` - ${subsubfolder.Name}`}));
      });
   }

   const startTranscriptionOperation = async () => {
      setWorkerState({isWorking: true, hasError: false});
      try {
         await transcriptionApi.startAutomatedTranscription(Hash);
         updateCaptionDataState(await captionApi.getStatus(MediaId));
         setWorkerState({isWorking: false, hasError: false});
      } catch (e) {
         setWorkerState({isWorking: false, hasError: true});
      }
   };

   const modalBody =
      (
         <div className="form-body themeable-section">
            {workerState.hasError && (
               <StyledWarning>
                  {aiRequestWasTooLarge ? t('details.generate.requestTooLargeErrorText') : (aiRequestContentFiltered ? t('details.generate.contentFilterdErrorText') : t('general.errorText'))}
               </StyledWarning>
            )}
            {!loadingCaptionData.isWorking && config.featureSwitches.EnableAIMediaSummary && IsVideo && (
               <MediaDetailsGenerateMenu
                  minMax={minMax}
                  canModifyMinMax={isDev}
                  setMinMaxCallback={setMinMaxCallback}
                  captionState={captionStatus}
                  onGenerateCallback={(generateType: MediaDetailGenerationTypes) => {
                     void onGenerateMediaDetailsCallback(generateType);
                  }}
                  onStartTranscriptionCallback={() => {
                     void startTranscriptionOperation();
                  }}
               />
            )}
            <div className="form-field-listing">
               <div className="form-field-group">
                  <label>
                     {t('details.title.label')}
                     <TextInput
                        value={decode(title)}
                        errorText={titleErrorText}
                        onChange={onTitleChange}
                        themeName={theme}
                        placeholder={t('details.title.placeholder', {0: config.watchConfig.maxTitleLength})}
                        required={true}
                        tabIndex={0}
                     />
                  </label>
               </div>
               <div className="form-field-group">
                  <label>
                     {t('details.description.label')}
                     <TextInput
                        testId="edit-details-modal-description"
                        sharedId="edit-details-modal-description"
                        value={decode(description)}
                        errorText={descriptionErrorText}
                        onChange={onDescriptionChange}
                        themeName={theme}
                        rows={5}
                        placeholder={t('details.description.placeholder', {0: config.watchConfig.maxDescriptionLength})}
                        tabIndex={0}
                        disabled={!MediaId}
                     />
                  </label>
               </div>
               {supportsChapters() && (
                  <MediaDetailsChapters
                     generatedChapters={localMediaData.chapters}
                     onChaptersUpdateCallback={onChaptersUpdate}
                     onChaptersInputErrorCallback={onChaptersInputError}
                  />
               )}
               <div className="form-field-group">
                  <SelectInput
                     label={t('details.locationLabel')}
                     value={parentFolderHash}
                     options={folderOptions}
                     onChange={updateParentFolder}
                     themeName={theme}
                     isFullWidth={true}
                  />
               </div>
               <div className="button-group">
                  {cancelButton}
                  {saveButton}
               </div>
            </div>
         </div>
      );

   return (
      <BasicModalPortal
         visible={true}
         title="Edit Details"
         width={editDetailsModalWidth}
         onClose={workerState.isWorking ? null : onClose}
         themeName={theme}
         isLoading={workerState.isWorking}
      >
         <div className="modal-body">
            {modalBody}
         </div>
      </BasicModalPortal>
   );
};

export interface IEditDetailsProps {
   onClose(): void;
}

export interface IStateMappedProps {
   conversations: IConversationData[];
   Hash: string;
   MediaId: string;
   Title: string;
   Description: string;
   ParentFolder: Optional<IMinimalFolder>;
   OwnerLibrary: Optional<IBaseLibrary>;
   IsVideo: boolean;
   ContentType: string;
   Duration: string;
   Chapters?: IMediaChapters;
   captionData: ICaptionData[];
}

const mapStatesToProps = (
   {onClose}: IEditDetailsProps,
   {conversations}: Partial<IStateMappedProps>,
   {Hash, MediaId, Title, Description, ParentFolder, OwnerLibrary, IsVideo, ContentType, Duration, Chapters}: Partial<IStateMappedProps>,
   {captionData}: Partial<IStateMappedProps>
): IStateMappedProps & IEditDetailsProps => ({onClose, conversations, Hash, MediaId, Title, Description, ParentFolder, OwnerLibrary, IsVideo, ContentType, Duration, Chapters, captionData});

export default withMemoizedContexts(mapStatesToProps, conversationsStore, mediaStore, captionDataStore)(EditDetailsModalBase);
